import React from "react";
import PropTypes from "prop-types";
import { STATES } from "utils/enum";
import { connect } from "react-redux";
import { Formik, Form, Field, useField, useFormikContext } from "formik";
import FormikFormField from "components/forms/formik-form-field";
import FormikCheckbox from "components/forms/formik-checkbox";
import FormikLeftAlignedCheckbox from "components/forms/formik-checkbox-left-aligned";
import FormikDateField from "components/forms/formik-date-field";
import PhoneNumberField from "components/forms/phone-number-field";
import SearchableDropdown from "components/searchable-dropdown";
import HasRolePermission from "components/shared/has-role-permission";
import IoIosInformationCircleOutline from "react-icons/lib/io/ios-information";
import { Tooltip } from "components/ui/tool-tip/tooltip";
import { useLocation } from "react-router-dom";
import { get } from "utils/api";
import { useFetch } from "hooks";
import { propOr } from "ramda";
import companyFormValidationSchema from "./company-form-validation";

const CompanyFormBody = ({
  editing,
  teamOptions,
  companyOptions,
  isNonAdmin,
  companyId,
  handleCancel
}) => {
  const { values, setFieldValue, isValid, isSubmitting } = useFormikContext();
  const location = useLocation();

  const { response: insurances, fetchData: getCompanyInsurances } = useFetch({
    defaultValue: [],
    apiFn: companyGuid => get(`companies/${companyGuid}/insurances`)
  });

  React.useEffect(() => {
    if (companyId) getCompanyInsurances(companyId);
  }, [companyId]);

  const { response: topic_categories, fetchData: getTopicCategories } =
    useFetch({
      apiFn: () => get(`dme_portal/appointment_topic_categories`),
      transformResponse: propOr([], "topic_categories")
    });

  React.useEffect(() => {
    if (!editing) getTopicCategories();
  }, []);

  React.useEffect(() => {
    if (topic_categories && !values.topics_duration) {
      const initialTopicsDuration = topic_categories.map(topic => ({
        iPKID: 0,
        iMinutes: null,
        sTopicId: topic.topic_id,
        sTopicCategory: topic.topic_name
      }));
      setFieldValue("topics_duration", initialTopicsDuration);
    }
  }, [topic_categories]);

  const selectedParentOption = (companyOptions || []).find(
    c => c.value === values.company_relationships?.parent_guid
  );
  const parentPrefix = selectedParentOption?.insurance_prefix ?? values.company_name ?? "";

  return (
    <>
      <fieldset>
        <legend>Company</legend>
        <FormikFormField
          name="company_name"
          label="Company"
          disabled={isNonAdmin}
        />
        <HasRolePermission allowedRoles={["Administrator"]}>
          <FormikFormField
            name="company_friendly_name"
            label={
              <span>
                Friendly Name{" "}
                <Tooltip
                  id={`friendly_name`}
                  className="helpful-link-tooltip"
                  effect="solid"
                  message="Friendly name is what the IVR will read as the company name. Used to avoid pronouncing characters and symbols."
                >
                  <IoIosInformationCircleOutline size="1rem" />
                </Tooltip>
              </span>
            }
            disabled={isNonAdmin}
          />
        </HasRolePermission>
        <FormikFormField
          name="account_number"
          label="Nonfulfillment Account Number"
          disabled={isNonAdmin}
        />
        <FormikFormField
          name="wellsky_account_number"
          label="WellSky Account Number"
          disabled={isNonAdmin}
        />
        <PhoneFormField
          name="outbound_phone_number"
          label="Outbound Phone Number"
          disabled={isNonAdmin}
        />
        <FormikFormField
          label="Reimbursement multiplier"
          name="reimbursement_multiplier"
          disabled={isNonAdmin}
        />
        <FormikFormField
          label={
            <span>
              HCPC Eligibility Offset{" "}
              <Tooltip
                id={`hcpc_eligibility_offset`}
                className="helpful-link-tooltip"
                effect="solid"
                message="HCPC eligibility offset represents how many days a patient's line item eligibility date will be pushed when calculated from the order date and insurance rule. Negative values will set the eligibility date closer to the present."
              >
                <IoIosInformationCircleOutline size="1rem" />
              </Tooltip>
            </span>
          }
          name="hcpc_eligibility_offset"
          type="number"
        />
        <FormikFormField
          label="Team"
          name="team_name"
          component="select"
          disabled={isNonAdmin}
        >
          <option />
          {teamOptions.map(({ value, text }) => (
            <option key={value} value={value}>
              {text}
            </option>
          ))}
        </FormikFormField>
        <FormikFormField
          label="PAP Resuppy Email"
          name="pap_resupply_email_address"
          disabled={isNonAdmin}
        />
        <PhoneFormField
          name="settings.sVoicemailCallbackNumber"
          label={
            <span>
              Voicemail Callback Number
              <Tooltip
                id={`sVoicemailCallbackNumber`}
                className="helpful-link-tooltip"
                effect="solid"
                message="If filled out, will leave this number in voicemails instead of the number we called from."
              >
                <IoIosInformationCircleOutline size="1rem" />
              </Tooltip>
            </span>
          }
        />
        <HasRolePermission allowedRoles={["Administrator"]}>
          <div style={{ margin: "10px 0px" }}>
            <FormikCheckbox label="Include Pricing" name="include_pricing" />
            <FormikCheckbox label="Uses Fulfillment" name="uses_fulfillment" />
            <FormikCheckbox
              label="Purchases equipment from S3"
              name="s3_middleman"
            />
            <FormikCheckbox label="Tracks Compliance" name="tracks_compliance" />
            <FormikCheckbox
              label="Review Autofit Results"
              name="review_autofit_results"
            />
            <FormikCheckbox label="Agent Calling" name="settings.bAICalling" />
          </div>
        </HasRolePermission>
      </fieldset>
      <HasRolePermission allowedRoles={["Administrator"]}>
        {values?.uses_fulfillment ? (
          <fieldset>
            <legend>Fulfillment companies</legend>
            <div>
              <div>
                {values?.uses_fulfillment &&
                values?.branches?.some(
                  x => x.branch_name?.toLowerCase() !== "unassigned"
                ) &&
                !location?.pathname.includes("/companies/new") ? (
                  <FormikCheckbox
                    label="Uses Branch Level Fulfillment Account Numbers"
                    name="bUsesBranchFulfillment"
                  />
                ) : (
                  <div
                    style={{
                      display: "inline",
                      color: "red",
                      fontWeight: "bold"
                    }}
                  >
                    Fulfillment currently only available at the company level.
                    In order to use branch level fulfillment, this company must
                    first have branches.
                  </div>
                )}
                {values?.uses_fulfillment &&
                values?.bUsesBranchFulfillment ? (
                  <div
                    style={{
                      display: "inline",
                      color: "red",
                      fontWeight: "bold"
                    }}
                  >
                    Please visit each branch and ensure account numbers are
                    accurate
                  </div>
                ) : (
                  ""
                )}
              </div>
              <FulfillmentRow
                checkboxName="uses_cpap_fulfillment"
                checkboxLabel="CPAP Dropship"
                accountName="cpap_account_number"
                accountLabel="CPAP Dropship Account Number"
                isNonAdmin={isNonAdmin}
              />
              <FulfillmentRow
                checkboxName="uses_independence_fulfillment"
                checkboxLabel="Independence Medical"
                accountName="independance_account_number"
                accountLabel="Independence Dropship Account Number"
                isNonAdmin={isNonAdmin}
              />
              <FulfillmentRow
                checkboxName="uses_internal_fulfillment"
                checkboxLabel="Internal"
                accountName="internal_account_number"
                accountLabel="Internal Account Number"
                isNonAdmin={isNonAdmin}
              />
              <FulfillmentRow
                checkboxName="uses_mckesson_fulfillment"
                checkboxLabel="McKesson"
                accountName="mckesson_account_number"
                accountLabel="McKesson Account Number"
                isNonAdmin={isNonAdmin}
              />
              <FulfillmentRow
                checkboxName="uses_ppm_fulfillment"
                checkboxLabel="PPM"
                accountName="ppm_account_number"
                accountLabel="PPM Account Number"
              />
              <FulfillmentRow
                checkboxName="uses_vgm_fulfillment"
                checkboxLabel="VGM"
                accountName="vgm_account_number"
                accountLabel="VGM Account Number"
                isNonAdmin={isNonAdmin}
              />
            </div>
          </fieldset>
        ) : (
          ""
        )}
      </HasRolePermission>
      <fieldset>
        <legend>Insurance</legend>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <FormikFormField
            label="NPI"
            name="insurance.npi"
            disabled={isNonAdmin}
          />
          <FormikFormField
            label="Tax Id"
            name="insurance.tax_id"
            disabled={isNonAdmin}
          />
          <FormikFormField
            label="Taxonomy Code"
            name="insurance.taxonomy_code"
            disabled={isNonAdmin}
          />
        </div>
      </fieldset>
      <HasRolePermission allowedRoles={["Administrator"]}>
        <fieldset>
          <legend>Company Status</legend>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(5, auto)",
              gridGap: 10
            }}
          >
            <FormikCheckbox label="Active" name="company_active" />
            <FormikCheckbox
              label="Access On Hold"
              name="company_access_hold"
              disabled={isNonAdmin}
            />
            <FormikCheckbox
              label="Outreach On Hold"
              name="company_outreach_hold"
              disabled={isNonAdmin}
            />
            <FormikCheckbox
              label="Review Orders"
              name="order_review"
              disabled={isNonAdmin}
            />
            <FormikCheckbox
              label="Autofill Order Line Items"
              name="autofill_order_line_items"
              disabled={isNonAdmin}
            />
            <FormikCheckbox
              label="Software As Service"
              name="software_as_service"
              disabled={isNonAdmin}
            />
            <FormikCheckbox
              label="Contractual Text Consent"
              name="AutoTextConsent"
              disabled={isNonAdmin}
            />
            <FormikCheckbox
              label="Contracted Viewing"
              name="allows_contracted_viewing"
              disabled={isNonAdmin}
            />
            <div>
              <FormikCheckbox
                label="Allow Contracted Inbound"
                name="settings.bAllowsContractedInbound"
                disabled={isNonAdmin}
              />
              <Tooltip
                id={`contracted_inbound`}
                className="helpful-link-tooltip"
                effect="solid"
                message={
                  <div>
                    When left unchecked, will let patients marked as
                    &quot;Offshore Limited&quot; go to the IVR (if Queue Can
                    Transfer
                    <br />
                    to IVR is checked) or leave a voicemail. This is so they do
                    not potentially connect to an offshore coach.
                  </div>
                }
              >
                <IoIosInformationCircleOutline size="1rem" />
              </Tooltip>
            </div>

            <FormikCheckbox
              label="Philips Recall Prompt"
              name="phillips_recall_prompt"
              disabled={isNonAdmin}
            />
            <FormikCheckbox
              label="Records Calls"
              name="settings.bRecordsCalls"
              disabled={isNonAdmin}
            />
            <FormikCheckbox
              label="Queue Can Transfer to IVR"
              name="settings.bAllowIVRTransferFromQueue"
              disabled={isNonAdmin}
            />

            <div style={{ display: "flex" }}>
              <Tooltip
                id="billed_elig_qty"
                className="helpful-link-tooltip"
                effect="solid"
                message={
                  "Use billed quantity for eligibility calculation if checked or unchecked to use shipped quantity for eligiblity."
                }
              >
                <IoIosInformationCircleOutline size="1rem" />
              </Tooltip>
              <FormikCheckbox
                label="Billed Quantity for Eligibility"
                name="settings.bHCPCEligibilityBilledQty"
              />
            </div>
            <FormikCheckbox
              label="Automate Welcome Email"
              name="settings.bAutomateResupplyEmail"
            />
            <FormikCheckbox
              label="Ignores Expired Form Task"
              name="settings.bSkipExpiredFormTask"
            />
            <FormikCheckbox
              label="Automate Daily Technology Outreach"
              name="settings.bAutomatedTechOutreach"
            />
            <FormikCheckbox
              label="Default Dropship Value(New Inventory Items)"
              name="settings.bDropshipDefaultForCompanyInventory"
            />
            <div style={{ display: "flex" }}>
              <Tooltip
                id={`opt_out_dnc`}
                className="helpful-link-tooltip"
                effect="solid"
                message="When checked, patients who opt out of IVR/Text/Email will be set as Do Not Contact instead of Manual Call."
              >
                <IoIosInformationCircleOutline size="1rem" />
              </Tooltip>
              <FormikCheckbox
                label="Opt Out Yields DNC"
                name="settings.bOptOutYieldsDNC"
              />
            </div>
            <FormikCheckbox
              label="Enable Autoship Email"
              name="settings.bEnableAutoshipEmail"
            />
          </div>
          <div style={{ marginTop: "10px" }}>
            <FormikCheckbox
              label="Deductible Renewing Reminder"
              name="settings.bNotifyDeductibleRenewingJanuary"
            />
            <Tooltip
              id={`deductible_renewing_reminder`}
              className="helpful-link-tooltip"
              effect="solid"
              message={
                <div>
                  When checked, will remind patients that deductibles will reset
                  at the beginning of the new year. Will only affect outreach
                  <br />
                  that happens from October through December. Set the last day
                  patients can order or leave it blank for a generic message.
                </div>
              }
            >
              <IoIosInformationCircleOutline size="1rem" />
            </Tooltip>
            {values?.settings?.bNotifyDeductibleRenewingJanuary && (
              <FormikDateField
                label="Last Day to Order"
                name="settings.dDeductibleReminder"
              />
            )}
          </div>
        </fieldset>
        <fieldset>
          <legend>Order Loading</legend>
          <FormikCheckbox
            label="Use Order Loading"
            name="order_loading"
            disabled={isNonAdmin}
          />
          <FormikCheckbox
            label="Email"
            name="order_loading_email"
            disabled={isNonAdmin}
          />
          <FormikCheckbox
            label="IVR"
            name="order_loading_IVR"
            disabled={isNonAdmin}
          />
          <FormikCheckbox
            label="Mobile App"
            name="order_loading_mobile_app"
            disabled={isNonAdmin}
          />
          <FormikCheckbox
            label="Patient Portal"
            name="order_loading_patient_portal"
            disabled={isNonAdmin}
          />
          <FormikCheckbox
            label="Web App"
            name="order_loading_web_app"
            disabled={isNonAdmin}
          />
        </fieldset>
        <fieldset>
          <legend>Company Integration</legend>
          <FormikCheckbox
            label="Bonafide Integration"
            name="bonafide_api"
            disabled={isNonAdmin}
          />
          <FormikCheckbox
            label="Niko Health Integration"
            name="nikohealth_api"
            disabled={isNonAdmin}
          />
          <FormikCheckbox
            label="RA Integration"
            name="ra_integration"
            disabled={isNonAdmin}
          />
        </fieldset>
        <fieldset>
          <legend>Patient Type Management</legend>
          {values?.patient_type_settings?.map((x, idx) => (
            <div
              key={idx}
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr",
                borderBottom: "1px solid #ccc",
                marginBottom: 10,
                gridGap: 20
              }}
            >
              <FormikCheckbox
                label={`Manages ${x.sPatientTypeLabel}`}
                name={`patient_type_settings[${idx}].bActive`}
                disabled={isNonAdmin}
              />
              <FormikFormField
                label={
                  <span>
                    {`${x.sPatientTypeLabel} Outreach Offset `}
                    <Tooltip
                      id={`${x.sPatientTypeName}_tooltip`}
                      className="helpful-link-tooltip"
                      effect="solid"
                      message="Outreach offset represents how many days a patient's outreach date will pushed upon being created."
                    >
                      <IoIosInformationCircleOutline size="1rem" />
                    </Tooltip>
                  </span>
                }
                name={`patient_type_settings[${idx}].iOutreachOffset`}
                disabled={isNonAdmin}
              />
              <div style={{ display: "flex", flexDirection: "column" }}>
                <PhoneFormField
                  name={`patient_type_settings[${idx}].phone_number`}
                  label={
                    <span>
                      {`${x.sPatientTypeLabel} Phone Number `}
                      <Tooltip
                        id={`${x.sPatientTypeName}_outreach_number`}
                        className="helpful-link-tooltip"
                        effect="solid"
                        message="Outreach Twillio phone number used for dialing outbound calls and directs call to companies team"
                      >
                        <IoIosInformationCircleOutline size="1rem" />
                      </Tooltip>
                    </span>
                  }
                  disabled={isNonAdmin}
                />
                <PhoneFormField
                  name={`patient_type_settings[${idx}].verified_phone_number`}
                  label={
                    <span>
                      {`${x.sPatientTypeLabel} Verified Twillio Number `}
                      <Tooltip
                        id={`${x.sPatientTypeName}_outreach_number_v`}
                        className="helpful-link-tooltip"
                        effect="solid"
                        message="Outreach Twillio phone number used for dialing outbound calls and directs call to companies team"
                      >
                        <IoIosInformationCircleOutline size="1rem" />
                      </Tooltip>
                    </span>
                  }
                  disabled={isNonAdmin}
                />
              </div>
            </div>
          ))}
        </fieldset>
        <fieldset>
          <legend>Product Lines</legend>
          <ProductLineFields productLines={values?.product_lines} />
        </fieldset>
      </HasRolePermission>
      <fieldset>
        <legend>Address</legend>
        <div className="address-fields">
          <FormikFormField
            label="Street Address"
            name="address.street_address"
            className="form-field street-address"
            style={{ gridColumn: "1 / -1" }}
            disabled={isNonAdmin}
          />
          <FormikFormField
            label="City"
            name="address.city"
            type="text"
            disabled={isNonAdmin}
          />
          <FormikFormField
            label="State"
            name="address.state"
            component="select"
            disabled={isNonAdmin}
          >
            <option />
            {STATES.map(({ value, text }) => (
              <option key={value} value={value}>
                {text}
              </option>
            ))}
          </FormikFormField>
          <FormikFormField
            label="Zip Code"
            name="address.zip"
            type="number"
            disabled={isNonAdmin}
          />
        </div>
      </fieldset>
      <HasRolePermission allowedRoles={["Administrator"]}>
        <fieldset>
          <legend>IVR</legend>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "auto auto auto auto ",
              gridTemplateRows: "auto",
              alignItems: "center",
              gridGap: 20,
              marginBottom: 10
            }}
          >
            <FormikCheckbox label="Ivr Company" name="ivr_company" />
            <FormikCheckbox
              label="Uses Branch Names"
              name="reads_branch_names"
            />
            <FormikCheckbox
              label="Company Handles Problems"
              name="company_handles_ivr_problems"
            />
            <FormikCheckbox
              label="Handles Orders"
              name="company_handles_orders"
            />
            <FormikCheckbox
              label="Verbal Response Allowed"
              name="is_verbal_IVR"
            />
            <div style={{ display: "flex" }}>
              <FormikCheckbox
                label="Read Address Line 2"
                name="settings.bReadAddressLine2"
              />
            </div>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              gridGap: 20
            }}
          >
            <FormikFormField
              label="Start Time (CST)"
              name="ivr_start"
              type="time"
              step="3600000"
            />
            <FormikFormField
              label="End Time (CST)"
              name="ivr_end"
              type="time"
              step="3600000"
            />
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              gridGap: 20
            }}
          >
            <PhoneFormField
              name="ivr_outbound_phone_number"
              label="IVR Outbound Number"
            />
            <PhoneFormField
              name="ivr_company_transfer_number"
              label="Problem Transfer Number"
            />
            <FormikFormField
              label="Daily Order Goal"
              name="ivr_order_goal"
              type="number"
            />
          </div>
          <fieldset
            style={{
              border: "none",
              borderTop: "1px solid #ddd"
            }}
          >
            <legend>Settings</legend>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr",
                gridGap: 20
              }}
            >
              <FormikFormField
                label="Number Of Patients To Pull From IVR Batch"
                name="number_of_patients_to_pull_from_ivr_batch"
                type="number"
              />
              <FormikFormField
                label="Number Of Patients To Call Per Request"
                name="number_of_patients_to_call_per_request"
                type="number"
              />
              <FormikFormField
                label="Max Total Threads Per Request"
                name="max_total_threads_per_request"
                type="number"
              />
            </div>
            <div>
              <FormikCheckbox label="Pause IVR" name="pause_ivr" />
              <FormikCheckbox
                label="Limit Patient Calls For Request"
                name="limit_patient_calls_for_request"
              />
            </div>
          </fieldset>
        </fieldset>
      </HasRolePermission>

      <HasRolePermission allowedRoles={["Administrator"]}>
        <fieldset>
          <legend>Variable Outreach</legend>
          <div style={{ display: "flex", justifyContent: "flex-start" }}>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                marginRight: "50px"
              }}
            >
              <span>
                Outreach Methods
                <Tooltip
                  id={`variable_outreach_methods`}
                  className="helpful-link-tooltip"
                  effect="solid"
                  message="Select which contact methods should use variable outreach logic on order creation."
                >
                  <IoIosInformationCircleOutline size="1rem" />
                </Tooltip>
              </span>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  flexWrap: "wrap"
                }}
              >
                <FormikLeftAlignedCheckbox
                  label="Manual Call"
                  name="settings.bVariableOutreachManualCall"
                />
                <FormikLeftAlignedCheckbox
                  label="IVR"
                  name="settings.bVariableOutreachIVR"
                />
                <FormikLeftAlignedCheckbox
                  label="Patient App / Text / Email"
                  name="settings.bVariableOutreachPatientPortal"
                />
              </div>
            </div>
            <div>
              <span>
                Product Types{" "}
                <Tooltip
                  id={`variable_outreach_products`}
                  className="helpful-link-tooltip"
                  effect="solid"
                  message="When a patient doesn't order an eligible selected item, push patient outreach for 30 days."
                >
                  <IoIosInformationCircleOutline size="1rem" />
                </Tooltip>
              </span>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  flexWrap: "wrap",
                  height: "80px"
                }}
              >
                <FormikLeftAlignedCheckbox
                  label="Mask"
                  name="settings.bContactIn30Mask"
                />
                <FormikLeftAlignedCheckbox
                  label="Seals / Cushions / Pillows"
                  name="settings.bContactIn30Seals"
                />
                <FormikLeftAlignedCheckbox
                  label="Chinstrap"
                  name="settings.bContactIn30Chinstrap"
                />
                <FormikLeftAlignedCheckbox
                  label="Filters"
                  name="settings.bContactIn30Filter"
                />
                <FormikLeftAlignedCheckbox
                  label="Headgear"
                  name="settings.bContactIn30Headgear"
                />
                <FormikLeftAlignedCheckbox
                  label="Tubing"
                  name="settings.bContactIn30Tubing"
                />
                <FormikLeftAlignedCheckbox
                  label="Waterchamber"
                  name="settings.bContactIn30Waterchamber"
                />
              </div>
            </div>
          </div>
        </fieldset>
      </HasRolePermission>
      <HasRolePermission allowedRoles={["Administrator"]}>
        <fieldset>
          <legend>Order Form</legend>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <hr />
            <div>
              <FormikCheckbox
                label="Uses Address Change Task"
                name="uses_address_change_task"
              />
              <FormikCheckbox
                label="Uses Insurance Change Task"
                name="uses_insurance_change_task"
              />
              <FormikCheckbox
                label="Uses Physician Change Task"
                name="uses_physician_change_task"
              />
              <FormikCheckbox
                label="Uses Generic Item Task"
                name="uses_generic_item_task"
              />
              <FormikCheckbox label="Therapy Issue" name="therapy_issue" />
              <FormikCheckbox
                label="Allow 30 Day Orders"
                name="order_form_settings.bAllow30DayOrders"
              />
            </div>
            <hr />

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr",
                gridGap: 10
              }}
            >
              <span>
                <Tooltip
                  id="zero-billed-headgear"
                  place="right"
                  effect="solid"
                  className="helpful-link-tooltip"
                  message="Only for companies that require headgear with masks. Check if headgear billing code added to order should be zero billed, this also changes how we load headgear on orders where mask is requested without headgear"
                >
                  <IoIosInformationCircleOutline size="1rem" />
                </Tooltip>
                <FormikCheckbox
                  label="Checks for zero billed headgear"
                  name="check_headgear_zero_billed"
                />
              </span>
              <FormikCheckbox
                label="Load items from patients current equipment"
                name="load_items_from_current_equipment"
              />
              <span>
                <FormikFormField
                  label="Days to allow future eligible items"
                  name="settings.iDaysAllowPreorder"
                  type="number"
                  style={{ width: "50px" }}
                />
              </span>
            </div>
          </div>
        </fieldset>
      </HasRolePermission>
      <fieldset>
        <legend>Administration</legend>
        <HasRolePermission allowedRoles={["Administrator"]}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gridGap: 10 }}>
            <FormikFormField
              label="Parent Company"
              name="company_relationships.parent_guid"
              component="select"
              disabled={isNonAdmin}
            >
              <option />
              {(companyOptions || []).map(({ value, text }) => (
                <option key={value} value={value}>
                  {text}
                </option>
              ))}
            </FormikFormField>
            <span className="form-field">
              <label className="form-field-label">Parent Prefix</label>
              <input
                type="text"
                className="input-field"
                value={parentPrefix}
                readOnly
                disabled
              />
            </span>
          </div>
        </HasRolePermission>
        <FormikDateField
          label="Outreach Start Date"
          name="service_start_date"
          disabled={isNonAdmin}
        />

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gridGap: 10
          }}
        >
          <FormikFormField
            label="Outreach Start Time (CST)"
            name="outreach_start_time"
            type="time"
            step="3600000"
            disabled={isNonAdmin}
          />
          <FormikFormField
            label="Outreach End Time (CST)"
            name="outreach_end_time"
            type="time"
            step="3600000"
            disabled={isNonAdmin}
          />
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gridGap: 10
          }}
        >
          <FormikFormField
            label="Office Hours Start"
            name="office_hours_start"
            type="time"
            step="3600000"
            disabled={isNonAdmin}
          />
          <FormikFormField
            label="Office Hours End"
            name="office_hours_end"
            type="time"
            step="3600000"
            disabled={isNonAdmin}
          />
        </div>
      </fieldset>
      <fieldset>
        <legend>PDF Signatures</legend>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr" }}>
          <div style={{ display: "flex", alignItems: "center" }}>
            <FormikCheckbox
              label="Custom Reminder Days"
              name="hasCustomReminderDays"
            />
            <FormikFormField
              name="customReminderDays"
              type="number"
              style={{ width: "50px" }}
              disabled={!values?.hasCustomReminderDays}
            />
          </div>
        </div>
      </fieldset>
      <fieldset>
        <legend>Importer Settings</legend>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gridGap: 10
          }}
        >
          <FormikCheckbox label="Uses Payor ID" name="uses_payor_id" />
          <FormikCheckbox
            label="Demographics File Only Updates Existing Accounts"
            name="demographics_only_update_existing"
          />
          <div>
            <label>New Insurance Template</label>
            <SearchableDropdown
              disabled={isNonAdmin}
              options={[
                { text: "Medicare", value: "" },
                ...insurances.map(x => ({
                  text: x.sInsuranceCompanyName,
                  value: x.sMGInsuranceID
                }))
              ]}
              selected={values?.insurance_template_id}
              onSelect={value => {
                setFieldValue("insurance_template_id", value);
              }}
            />
          </div>
        </div>
      </fieldset>
      <fieldset>
        <legend>Appointment Settings</legend>
        <FormikCheckbox
          label="Weekends Available"
          name="allow_weekend_appointments"
        />
        <fieldset
          style={{
            border: "none",
            borderTop: "1px solid #ddd"
          }}
        >
          <legend>Topics Duration</legend>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gridGap: 10
            }}
          >
            {values?.topics_duration?.map((x, idx) => (
              <FormikFormField
                key={`topic_${idx}`}
                label={x.sTopicCategory}
                name={`topics_duration[${idx}].iMinutes`}
                type="number"
                min={15}
                max={255}
                step={15}
              />
            ))}
          </div>
        </fieldset>
      </fieldset>
      <div className="text-right">
        {handleCancel && (
          <button
            type="button"
            className="button alert"
            onClick={handleCancel}
            style={{ marginRight: "20px" }}
          >
            Cancel
          </button>
        )}
        <button
          className="button"
          type="submit"
          disabled={!isValid || isSubmitting}
        >
          Submit
        </button>
      </div>
    </>
  );
};

CompanyFormBody.propTypes = {
  editing: PropTypes.bool,
  teamOptions: PropTypes.array.isRequired,
  companyOptions: PropTypes.array,
  isNonAdmin: PropTypes.bool,
  companyId: PropTypes.string,
  handleCancel: PropTypes.func
};

const PhoneFormField = ({ name, label, disabled }) => {
  const [, meta] = useField(name);
  const hasError = typeof meta.error === "string";
  return (
    <span className="form-field">
      {label && <label className="form-field-label">{label}</label>}
      {hasError && <span className="form-field-error">{meta.error}</span>}
      <Field
        name={name}
        component={PhoneNumberField}
        disabled={disabled}
        className="input-field"
      />
    </span>
  );
};
PhoneFormField.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  disabled: PropTypes.bool
};

const FulfillmentRow = ({
  checkboxName,
  checkboxLabel,
  accountName,
  accountLabel,
  isNonAdmin
}) => {
  const { values } = useFormikContext();
  return (
    <div
      style={{
        display: "flex",
        flexWrap: "no-wrap",
        alignItems: "center",
        justifyContent: "space-between",
        width: "35rem"
      }}
    >
      <FormikCheckbox
        label={checkboxLabel}
        name={checkboxName}
        disabled={isNonAdmin}
      />
      {!values?.bUsesBranchFulfillment && values?.[checkboxName] ? (
        <FormikFormField
          label={accountLabel}
          name={accountName}
          type="text"
          style={{ width: "20rem" }}
        />
      ) : (
        ""
      )}
    </div>
  );
};

FulfillmentRow.propTypes = {
  checkboxName: PropTypes.string.isRequired,
  checkboxLabel: PropTypes.string.isRequired,
  accountName: PropTypes.string.isRequired,
  accountLabel: PropTypes.string.isRequired,
  isNonAdmin: PropTypes.bool
};

const ProductLineFields = ({ productLines }) => (
  <div>
    {productLines?.map(({ text }, index) => (
      <div key={index} className="variable-field">
        <div style={{ display: "flex", flexDirection: "column" }}>
          <h4>{text}</h4>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              alignItems: "center",
              gridGap: 10
            }}
          >
            <FormikCheckbox
              label="Active"
              name={`product_lines.${index}.active`}
            />
            <FormikFormField
              label={
                <span>
                  HCPC Eligibility Offset{" "}
                  <Tooltip
                    id={`product_line_offset_${index}`}
                    className="helpful-link-tooltip"
                    effect="solid"
                    message="Offset for this product line. Overrides the company-wide HCPC eligibility offset for this line only. Positive values shift eligibility and outreach later; negative values shift them earlier. Leave blank to use the company-wide value."
                  >
                    <IoIosInformationCircleOutline size="1rem" />
                  </Tooltip>
                </span>
              }
              name={`product_lines.${index}.hcpc_eligibility_offset`}
              type="number"
            />
            <FormikCheckbox
              label={
                <span>
                  Apply Offset to 30-Day Orders{" "}
                  <Tooltip
                    id={`product_line_offset_30day_${index}`}
                    className="helpful-link-tooltip"
                    effect="solid"
                    message="When checked, the HCPC eligibility offset is also applied to this product line's 30-day supply outreach dates (60- and 90-day orders always apply the offset). Leave unchecked to skip the offset on 30-day orders."
                  >
                    <IoIosInformationCircleOutline size="1rem" />
                  </Tooltip>
                </span>
              }
              name={`product_lines.${index}.allow_offset_for_30_days`}
            />
          </div>
        </div>
      </div>
    ))}
  </div>
);

ProductLineFields.propTypes = {
  productLines: PropTypes.array
};

const CompanyForm = ({
  initialValues,
  onSubmit,
  editing,
  teamOptions,
  companyOptions,
  userRole,
  isNonAdmin,
  companyId,
  handleCancel
}) => (
  <Formik
    initialValues={initialValues}
    validationSchema={companyFormValidationSchema}
    onSubmit={onSubmit}
    enableReinitialize
    validateOnMount
  >
    <Form className="company-form">
      <CompanyFormBody
        editing={editing}
        teamOptions={teamOptions}
        companyOptions={companyOptions}
        userRole={userRole}
        isNonAdmin={isNonAdmin}
        companyId={companyId}
        handleCancel={handleCancel}
      />
    </Form>
  </Formik>
);

CompanyForm.propTypes = {
  initialValues: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
  editing: PropTypes.bool,
  teamOptions: PropTypes.array.isRequired,
  companyOptions: PropTypes.array,
  userRole: PropTypes.string.isRequired,
  isNonAdmin: PropTypes.bool,
  companyId: PropTypes.string,
  handleCancel: PropTypes.func
};

export default connect((state, { userRole }) => ({
  isNonAdmin: userRole !== "Administrator"
}))(CompanyForm);
